import { Button, CopyButtonWithTooltip, StatusTagEnum, darkColors } from '@tryhackme/thm-ui-components';
import type React from 'react';
import { type PropsWithChildren, useCallback, useState } from 'react';
import { ThemeProvider, useTheme } from 'styled-components';
import { useAppDispatch } from 'src/app/hooks';
import { setActiveMachine } from 'src/app/reducers/room/room.reducer';
import { ToolTip } from 'src/common/components';
import { useExtendVmTime } from 'src/common/hooks';
import type { RunningInstance } from 'src/common/interfaces/instances';
import type { RoomTask } from 'src/common/interfaces/tasks';
import InfoSvg from 'src/images/svgs/info.svg';
import { ModalActiveMachineInfo, TerminateMachineModal } from '../../active-machine-info/components';
import { ExpiredMachineModal } from '../../active-machine-info/components/expired-machine-modal';
import { MachineExpiringWrapper } from '../../machine-expiring';
import { StyledImage, StyledIpAddress, StyledIpAddressWrapper, StyledMachineWrapper, StyledPropItem, StyledPropsList, StyledPropsListAndCtaWrapper, StyledTag, StyledTagTitle, StyledTitle, StyledToolbar } from './machine.styles';
type MachineProps = {
  roomCode: string;
  variant: 'attackbox' | 'target';
  ipAddress: string;
  title?: string;
  expires?: string;
  runningInstance: RunningInstance;
  hideIpAddress?: boolean;
  activeVmTask?: RoomTask;
};
export const Machine: React.FC<PropsWithChildren<MachineProps>> = ({
  roomCode,
  variant,
  ipAddress,
  title,
  expires,
  runningInstance,
  hideIpAddress,
  activeVmTask
}) => {
  const theme = useTheme();
  const [isOpenTerminateMachineModal, setIsOpenTerminateMachineModal] = useState(false);
  const {
    handleExtendVmTime,
    isLoading: isExtendVmTimeLoading
  } = useExtendVmTime();
  const [openExpiredMachineModal, setOpenExpiredMachineModal] = useState<boolean>(false);
  const isAttackBox = variant === 'attackbox';
  const isTarget = variant === 'target';
  const dispatch = useAppDispatch();
  const onExpireActiveMachine = useCallback(() => {
    dispatch(setActiveMachine());
  }, [dispatch]);
  return <>
      <StyledMachineWrapper data-sentry-element="StyledMachineWrapper" data-sentry-source-file="machine.tsx">
        <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1.6rem',
        width: '100%'
      }}>
          <span>
            <StyledTitle data-sentry-element="StyledTitle" data-sentry-source-file="machine.tsx">
              {isAttackBox ? 'Attacker machine' : `Target machine${activeVmTask ? ` - Task ${activeVmTask.taskNo}` : ''}`}
            </StyledTitle>
            <ToolTip toolTipText={isAttackBox ? 'AttackBox is an Ubuntu Virtual Machine hosted in the cloud, allowing you to complete many tasks in our rooms. Think of the AttackBox as your virtual computer, which you would use to conduct a security engagement.' : 'Target/task machines are specific VMs that you interact with during your tasks. They are the targets you will exploit or work with to solve challenges. Each Task Machine has a unique IP address.'} $contentMaxWidth="33rem" $fontSize="tiny" side="right" data-sentry-element="ToolTip" data-sentry-source-file="machine.tsx">
              <StyledImage src={InfoSvg} alt="Machine info " data-sentry-element="StyledImage" data-sentry-source-file="machine.tsx" />
            </ToolTip>
          </span>
          <StyledPropsListAndCtaWrapper data-sentry-element="StyledPropsListAndCtaWrapper" data-sentry-source-file="machine.tsx">
            <StyledPropsList data-sentry-element="StyledPropsList" data-sentry-source-file="machine.tsx">
              <StyledPropItem data-sentry-element="StyledPropItem" data-sentry-source-file="machine.tsx">
                Status:
                <StyledTag status={StatusTagEnum.GREEN} size="default" data-sentry-element="StyledTag" data-sentry-source-file="machine.tsx">
                  <StyledTagTitle data-sentry-element="StyledTagTitle" data-sentry-source-file="machine.tsx">{isAttackBox ? 'Connected via Attackbox' : 'On'}</StyledTagTitle>
                </StyledTag>
              </StyledPropItem>
              {isTarget && title && <StyledPropItem>
                  Title:
                  <span>{title}</span>
                </StyledPropItem>}
              <StyledPropItem data-sentry-element="StyledPropItem" data-sentry-source-file="machine.tsx">
                {isAttackBox ? 'Your machine IP Address:' : 'Target IP Address:'}
                <StyledIpAddressWrapper data-sentry-element="StyledIpAddressWrapper" data-sentry-source-file="machine.tsx">
                  <StyledIpAddress data-sentry-element="StyledIpAddress" data-sentry-source-file="machine.tsx">{ipAddress}</StyledIpAddress>
                  {!hideIpAddress && <CopyButtonWithTooltip value={ipAddress} />}
                </StyledIpAddressWrapper>
              </StyledPropItem>
              {isTarget && <StyledPropItem>
                  Expires:
                  <span>{expires}</span>
                </StyledPropItem>}
            </StyledPropsList>
            {isAttackBox && <ThemeProvider theme={{
            ...theme,
            colors: darkColors
          }}>
                <Button variant="solid" color="error" onClick={() => setIsOpenTerminateMachineModal(true)}>
                  Terminate
                </Button>
              </ThemeProvider>}
          </StyledPropsListAndCtaWrapper>
          {isTarget && <StyledToolbar>
              <ModalActiveMachineInfo roomCode={roomCode} />
              <ThemeProvider theme={{
            ...theme,
            colors: darkColors
          }}>
                <Button variant="outlined" color="secondary" isLoading={isExtendVmTimeLoading} onClick={() => {
              handleExtendVmTime(runningInstance.id).catch(() => {});
            }}>
                  Add 1 hour
                </Button>
                <Button variant="solid" color="error" onClick={() => setIsOpenTerminateMachineModal(true)}>
                  Terminate
                </Button>
              </ThemeProvider>
            </StyledToolbar>}
        </div>
      </StyledMachineWrapper>
      <TerminateMachineModal runningInstance={runningInstance} open={isOpenTerminateMachineModal} onOpenChange={setIsOpenTerminateMachineModal} data-sentry-element="TerminateMachineModal" data-sentry-source-file="machine.tsx" />
      {isTarget && <>
          <ExpiredMachineModal open={openExpiredMachineModal} onOpenChange={setOpenExpiredMachineModal} onCloseCallback={onExpireActiveMachine} />
          <MachineExpiringWrapper activeMachine={runningInstance} />
        </>}
    </>;
};